import { FormState } from 'final-form';
import {
  AwardPointsInput,
  AwardVouchersInput,
  OfferFragment,
  ProductGroupFragment,
  Earning_Mechanism_Product_Counting_Attribute,
  Award_Points_Rounding_Strategy,
  AwardPointsPerInput,
  AwardExternalPointsInput,
  AwardExternalPointsFragment,
  AwardPointsFragment,
  AwardPointsPerFragment,
  AwardVouchersFragment,
  AwardTransactionMoneyDiscountFragment,
  AwardTransactionPercentDiscountFragment,
  AwardMoneyDiscountFragment,
  AwardPriceOverrideFragment,
  AwardBuyAndGetFragment,
  AwardProductDiscountFragment,
  AwardExternalFragment,
  SurveyAwardVouchersFragment,
  AwardExternalInput,
  ExternalAwardProvider,
} from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';

import {
  IUseGetOfferOperationHandlersReturns,
  useGetOfferOperationHandlers,
} from './operations/useGetOfferOperationHandlers';

export type AwardPointsFormValues = {
  points: number;
};

export type AwardVouchersFormValues = {
  voucheredOffer: Pick<OfferFragment, 'id' | 'name' | 'engagementType'>;
};

export type AwardPointsPerFormValues = {
  points: number;
  rounding: Award_Points_Rounding_Strategy;
};

export type AwardExternalPointsFormValues = {
  maximum: number;
};

export type AwardExternalFormValues = {
  provider: ExternalAwardProvider;
};

// TODO: Find all places where strings are used as keys instead of AwardType[key], use enum
export enum AwardType {
  AwardPoints = 'awardPoints',
  AwardVouchers = 'awardVouchers',
  AwardPointsPer = 'awardPointsPer',
  AwardExternalPoints = 'awardExternalPoints',
  AwardExternal = 'awardExternal',
  AwardNullable = 'awardNullable',
}

export type AwardInputType =
  | { awardPoints: AwardPointsInput }
  | { awardVouchers: AwardVouchersInput }
  | { awardPointsPer: AwardPointsPerInput }
  | { awardExternalPoints: AwardExternalPointsInput }
  | { awardExternal: AwardExternalInput }
  | null;

export type AwardPointsOrVouchersInput =
  | {
      awardPoints: AwardPointsInput;
    }
  | {
      awardVouchers: AwardVouchersInput;
    };

export type AwardPointsOrVouchersOrNullInput = AwardPointsOrVouchersInput | null;

export type ProductPurchaseFormValues = {
  eligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[];
  purchaseThreshold: number;
  countProductsBy: Earning_Mechanism_Product_Counting_Attribute;
};

export type SpendBonusFormValues = {
  accumulationThreshold: number;
  ineligibleProductGroups: Pick<ProductGroupFragment, 'id' | 'name'>[];
  stayVisibleWhileInProgress?: {
    isEnabled: boolean;
  };
};

export type OfferConfigureOperations = keyof ReturnType<typeof useGetOfferOperationHandlers>;
export type OperationVariables<T extends OfferConfigureOperations> = Required<
  NonNullable<Parameters<IUseGetOfferOperationHandlersReturns[T]>[0]>
>['variables'];

// Types for Offer configure step.
// TODO: refactor later
export type Operation<N = OfferConfigureOperations, V = any> = {
  name: N;
  variables: V;
};

export type GetConfigureFormOperations<T> = (
  offerId: string,
  offer: OfferFragment | undefined,
  values: T,
  dirtyFields: DirtyFieldsType,
) => Operation[];

export type GetOperations = (
  offerId: string,
  offer: OfferFragment | undefined,
  formValues: any,
  dirtyFields: DirtyFieldsType,
) => Operation[];

export type DirtyFieldsType<T = any> = FormState<T>['dirtyFields'];

export enum Flow_Types {
  Configure = 'CONFIGURE',
  Update = 'UPDATE',
}

export type InitialFormValues = {
  name: string;
  description?: string;
};

export type RefetchOffer = (silent?: boolean) => Promise<void>;

export type FormContainerProps = {
  campaignId?: string;
  offerId: string;
  isRefetching?: boolean;
  onRefetchOffer: RefetchOffer;
  offerLoading: boolean;
  setTabValue: (tab: CampaignComponentTabs) => void;
  currentFlowType: Flow_Types;
  onError: (error: Error) => void;
  onCancel: (hasUnsavedChanges?: boolean) => void;
  showExternalUrl?: boolean;
  showSurgeonGeneralsWarning?: boolean;
  showOrderAheadToggle?: boolean;
  type: OfferType;
  offer?: OfferFragment;
};

export type GetFormPropsHook<A, T> = (args: A) => T;

export type AwardFragment =
  | AwardVouchersFragment
  | AwardPointsFragment
  | AwardPointsPerFragment
  | AwardExternalPointsFragment
  | AwardTransactionMoneyDiscountFragment
  | AwardTransactionPercentDiscountFragment
  | AwardMoneyDiscountFragment
  | AwardPriceOverrideFragment
  | AwardBuyAndGetFragment
  | AwardProductDiscountFragment
  | SurveyAwardVouchersFragment // This one will be removed and AwardVouchersFragment will be used instead when BE implements it on their side
  | AwardExternalFragment;

export type AwardVouchersWithExpirationDaysFormValues = {
  voucheredOffer: Pick<OfferFragment, 'id' | 'name' | 'engagementType'>;
  isEnabled: boolean; // TODO should be renamed to isVoucherExpirationEnabled
  voucherExpirationDays?: number;
};

export type AwardFormValues = {
  awardType: AwardType;
  awardPoints?: AwardPointsFormValues;
  awardVouchers?: AwardVouchersWithExpirationDaysFormValues;
  awardPointsPer?: AwardPointsPerFormValues;
  awardExternalPoints?: AwardExternalPointsFormValues;
  awardExternal?: AwardExternalFormValues;
};

export type AwardPointsOrVouchersFormValues = {
  awardType: AwardType;
  awardPoints?: AwardPointsFormValues;
  awardVouchers?: AwardVouchersWithExpirationDaysFormValues;
};

export type FormPropsWithNextStepHandler<T> = T & { onNextStepButtonClick: () => void };
